// API Key Service for Nova Web

import type {
	ApiKey,
	ApiKeyListResponse,
	ApiKeyMetrics,
	CreateApiKeyDto,
	CreateApiKeyResponse,
	ProjectMetrics,
	UpdateApiKeyDto,
} from "../types/api-keys";
import { apiFetch } from "./apiFetch";

export const apiKeyService = {
	getAll: async (projectId: string): Promise<ApiKey[]> => {
		const response = await apiFetch<ApiKeyListResponse | ApiKey[]>(
			`/projects/${projectId}/api-keys`,
		);
		let keys: ApiKey[] = [];
		// Backend returns { success: true, data: ApiKeyResponse[], meta: {...} }
		if (Array.isArray(response)) {
			keys = response;
		} else if (
			(response as any).data &&
			Array.isArray((response as any).data)
		) {
			// Backend returns { data: [...], meta: {...} } or { success: true, data: [...], meta: {...} }
			keys = (response as any).data ?? [];
		} else if ((response as ApiKeyListResponse).keys) {
			// Legacy format: { keys: [...], total: number }
			keys = (response as ApiKeyListResponse).keys ?? [];
		}
		// Map keyPrefix to keyPreview for frontend compatibility
		// Show full keyPrefix without masking (no dots) so users can copy it
		return keys.map((key) => {
			const cleanKeyPrefix = key.keyPrefix
				? key.keyPrefix.replace(/•/g, "").trim()
				: key.keyPrefix;
			return {
				...key,
				// Ensure keyPrefix is clean (no dots) - strip any dots that might accidentally be there
				keyPrefix: cleanKeyPrefix,
				// Use keyPrefix directly (no masking) so users can copy it
				keyPreview: key.keyPreview || cleanKeyPrefix,
			};
		});
	},

	getById: async (projectId: string, keyId: string): Promise<ApiKey> => {
		const key = await apiFetch<ApiKey>(
			`/projects/${projectId}/api-keys/${keyId}`,
		);
		// Map keyPrefix to keyPreview for frontend compatibility
		// Show full keyPrefix without masking (no dots) so users can copy it
		const cleanKeyPrefix = key.keyPrefix
			? key.keyPrefix.replace(/•/g, "").trim()
			: key.keyPrefix;
		return {
			...key,
			// Ensure keyPrefix is clean (no dots) - strip any dots that might accidentally be there
			keyPrefix: cleanKeyPrefix,
			// Use keyPrefix directly (no masking) so users can copy it
			keyPreview: key.keyPreview || cleanKeyPrefix,
		};
	},

	create: async (
		projectId: string,
		data: CreateApiKeyDto,
	): Promise<CreateApiKeyResponse> => {
		return apiFetch<CreateApiKeyResponse>(`/projects/${projectId}/api-keys`, {
			method: "POST",
			body: JSON.stringify(data),
		});
	},

	update: async (
		projectId: string,
		keyId: string,
		data: UpdateApiKeyDto,
	): Promise<ApiKey> => {
		return apiFetch<ApiKey>(`/projects/${projectId}/api-keys/${keyId}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		});
	},

	revoke: async (projectId: string, keyId: string): Promise<void> => {
		await apiFetch<{ message: string }>(
			`/projects/${projectId}/api-keys/${keyId}`,
			{
				method: "DELETE",
			},
		);
	},

	getStats: async (
		projectId: string,
		keyId: string,
	): Promise<ApiKeyMetrics> => {
		return apiFetch<ApiKeyMetrics>(
			`/projects/${projectId}/api-keys/${keyId}/metrics`,
		);
	},

	getMetrics: async (
		projectId: string,
		keyId: string,
		period: "hour" | "day" | "week" | "month" = "day",
	): Promise<ApiKeyMetrics> => {
		return apiFetch<ApiKeyMetrics>(
			`/projects/${projectId}/api-keys/${keyId}/metrics?period=${period}`,
		);
	},

	getAllMetrics: async (
		projectId: string,
		period: "hour" | "day" | "week" | "month" = "day",
	): Promise<ProjectMetrics> => {
		return apiFetch<ProjectMetrics>(
			`/projects/${projectId}/metrics?period=${period}`,
		);
	},

	toggleActive: async (
		projectId: string,
		keyId: string,
		isActive: boolean,
	): Promise<ApiKey> => {
		return apiFetch<ApiKey>(`/projects/${projectId}/api-keys/${keyId}`, {
			method: "PATCH",
			body: JSON.stringify({ isActive }),
		});
	},
};
